import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { Text, UnstyledButton } from '@/shared/ui';
import type { PropertyResponseType } from '@services/housien/types';
import { MobileCardView } from '../MobileCardView';
import type { GroupAggregate, PropertyGroup } from '../../pages/property-grouping';
import { formatArea, formatMoney, PENDING_METRIC } from './group-metrics';

interface GroupedMobileListProps {
  /** The groups to render as sticky sections, already ordered. */
  groups: PropertyGroup[];
  /** Per-group aggregate stats; null when still pending. */
  aggregates: Map<string, GroupAggregate | null>;
  /** Card tap handler (navigates to the property). */
  onCardClick: (p: PropertyResponseType) => void;
  /** Reports whether a group key is collapsed. */
  isCollapsed: (key: string) => boolean;
  /** Toggles a group's collapsed state. */
  onToggleCollapsed: (key: string) => void;
  /** Translator for the MobileCardView specs. */
  t: (key: string) => string;
}

/**
 * Builds the one-line aggregate caption under a mobile section title: count,
 * average area and median price, each falling back to "—" while pending.
 *
 * @param aggregate - The group's aggregate (or null).
 * @param count - The group's property count.
 * @param t - Translator for the count and captions.
 * @returns The caption string.
 */
function captionLine(
  aggregate: GroupAggregate | null,
  count: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const parts = [t('list.grouping.propertyCount', { count })];
  if (aggregate) {
    parts.push(`${t('list.grouping.captionAverage')} ${formatArea(aggregate.avgTotalArea)}`);
    const median = aggregate.medianSalePrice != null ? aggregate.medianSalePrice : aggregate.medianRentalPrice;
    const caption = aggregate.medianSalePrice != null ? t('list.grouping.captionMedianSale') : t('list.grouping.captionMedianRental');
    parts.push(`${caption} ${formatMoney(median)}`);
  } else {
    parts.push(PENDING_METRIC);
  }
  return parts.join(' · ');
}

/**
 * The mobile grouped list: each group becomes a sticky section title (name, UF
 * and a single aggregate caption line) above its `MobileCardView` cards.
 * Tapping a title collapses or expands the section; there is no Resumo toggle
 * on mobile.
 *
 * @param props - The groups, aggregates, collapse state, and handlers.
 * @returns The grouped mobile list element.
 */
export const GroupedMobileList: React.FC<GroupedMobileListProps> = ({
  groups,
  aggregates,
  onCardClick,
  isCollapsed,
  onToggleCollapsed,
  t,
}) => {
  const { t: tt } = useTranslation('features/properties');

  return (
    <div data-testid="grouped-mobile-list" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {groups.map((group) => {
        const collapsed = isCollapsed(group.key);
        const agg = aggregates.has(group.key) ? aggregates.get(group.key)! : null;
        const count = agg?.count ?? group.items.length;

        return (
          <section key={group.key} data-testid="mobile-group-section">
            <UnstyledButton
              type="button"
              data-testid="mobile-group-header"
              aria-expanded={!collapsed}
              onClick={() => onToggleCollapsed(group.key)}
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                width: '100%',
                textAlign: 'left',
                padding: 'var(--space-2-5) var(--space-3)',
                background: 'var(--surface-card)',
                borderTop: '1px solid var(--border-subtle)',
                borderBottom: '1px solid var(--border-subtle)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              <span
                className="motion-safe:transition-transform"
                style={{ display: 'inline-flex', color: 'var(--text-secondary)', transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'var(--transition-standard)' }}
              >
                <ChevronDownIcon width={16} height={16} />
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', minWidth: 0 }}>
                  <Text as="span" style={{ fontSize: 'var(--text-app-subheading)', fontWeight: 'var(--font-semibold)' as unknown as number, color: 'var(--text-primary)' }}>
                    {group.label}
                  </Text>
                  {group.subLabel && (
                    <Text as="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}>{group.subLabel}</Text>
                  )}
                </div>
                <Text as="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>
                  {captionLine(agg, count, tt)}
                </Text>
              </div>
            </UnstyledButton>
            {!collapsed && (
              <div style={{ paddingTop: 'var(--space-3)' }}>
                <MobileCardView properties={group.items} onCardClick={onCardClick} t={t} />
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};
